import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Row,
  Col,
  Alert,
  Spinner
} from "reactstrap";
import { useDispatch, useSelector } from 'react-redux';
import { confirmPayment } from '../../store/actions';
import { format, parseISO } from 'date-fns';
import { ar } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { db } from "helpers/firebase/firebaseConfig";
import { collection, getDocs, query, limit } from "firebase/firestore";

const PayModal = ({ isOpen, toggleModal, transaction }) => {

  const dispatch = useDispatch();
  const transactions = useSelector((state) => state.transactions);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const prevTransactionsRef = useRef(transactions);
  const submitTimeoutRef = useRef(null);

  useEffect(() => {
    if (!isSubmitting) {
      prevTransactionsRef.current = transactions;
      return;
    }

    if (prevTransactionsRef.current !== transactions) {
      setIsSubmitting(false);
      setIsConfirmationOpen(false);
      toggleModal();
    }

    prevTransactionsRef.current = transactions;
  }, [transactions, isSubmitting, toggleModal]);

  useEffect(() => {
    return () => {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
    };
  }, []);
  
  // ✅ Now check for transaction AFTER all hooks
  if (!transaction) {
    return null;
  }

  // Get commission rates from transaction
  const appCommissionRate = Number(transaction.app_commission_rate || 0); // App commission is always 0
  const serviceProviderCommissionRate = Number(transaction.service_provider_commission_rate || 0);
  const paymentMethod = String(
    transaction?.booking?.paymentMethod || transaction?.booking?.payment_method || transaction?.payment_method || ""
  ).toLowerCase();
  const tamaraCommissionRate = paymentMethod === "tamara" ? Number(transaction.tamara_commission || 0) : 0;

  const formatDate = (date) => {
    if (!date) return 'غير متوفر';
    
    try {
      let parsedDate;
      
      if (typeof date === 'object' && 'seconds' in date) {
        parsedDate = new Date(date.seconds * 1000);
      } else if (typeof date === 'string') {
        parsedDate = parseISO(date);
      } else if (date instanceof Date) {
        parsedDate = date;
      } else {
        return 'تاريخ غير صالح';
      }
      
      return format(parsedDate, 'yyyy/MM/dd - hh:mm a', { locale: ar });
    } catch (error) {
      console.error('خطأ في تحويل التاريخ:', error);
      return 'تاريخ غير صالح';
    }
  };

  const handleConfirm = () => {
    const isRefundMarked = transaction?.needs_refund === true;
    const isFineDeducted =
      transaction?.fine_deducted === true ||
      String(transaction?.fine_deducted || "").toLowerCase() === "true";
    const bookingStatus = String(transaction?.booking?.booking_status || "").toLowerCase();
    const paymentStatus = String(
      transaction?.booking?.paymentStatus || transaction?.booking?.payment_status || ""
    ).toLowerCase();
    const paidValues = ["paid", "completed", "success", "succeeded"];

    if (isRefundMarked && !isFineDeducted) {
      toast.error("لا يمكن إنشاء فاتورة ERP لأن الفاتورة معلمة كاسترجاع ولم يتم تأكيد خصم الغرامة بعد.");
      return;
    }

    if (!isRefundMarked && (bookingStatus !== "completed" || !paidValues.includes(paymentStatus))) {
      toast.error("لا يمكن إنشاء الفاتورة: يجب أن تكون حالة الطلب مكتملة وحالة الدفع مدفوعة.");
      return;
    }
    

    setIsConfirmationOpen(true);
  };

  const handleProceed = () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    dispatch(confirmPayment(transaction));

    if (submitTimeoutRef.current) {
      clearTimeout(submitTimeoutRef.current);
    }

    submitTimeoutRef.current = setTimeout(() => {
      setIsSubmitting(false);
      toast.warning("ما زالت العملية قيد التنفيذ... يرجى الانتظار.");
    }, 20000);
  };

  const handleCancel = () => {
    setIsConfirmationOpen(false);
  };

  // ✅ Payment math for ERP invoice generation (platform's revenue)
  const VAT_RATE = 0.15;
  const fineDeductions = Number(transaction?.fine_deductions_total || 0);
  const grossAmount = Number(
    transaction?.total_after_discount_before_fine ||
      transaction?.total_after_discount ||
      0
  );
  
  // Extract service amount from gross (already includes VAT)
  const serviceVAT = grossAmount * (VAT_RATE / (1 + VAT_RATE)); // Extract VAT from gross
  const serviceNet = grossAmount - serviceVAT;
  
  // Calculate commissions on service net
  const appCommission = serviceNet * (appCommissionRate / 100);
  const serviceProviderCommission = serviceNet * (serviceProviderCommissionRate / 100);
  const tamaraCommission = serviceNet * (tamaraCommissionRate / 100);
  const totalCommission = appCommission + serviceProviderCommission + tamaraCommission;
  
  // VAT on commissions
  const commissionVAT = totalCommission * VAT_RATE;
  const totalPlatformRevenue = totalCommission + commissionVAT + fineDeductions;
  const netAfterFines =
    transaction?.net_after_fines !== undefined
      ? Number(transaction.net_after_fines)
      : Math.max(grossAmount - fineDeductions, 0);

  const cancellationActor = transaction?.cancelled_by;
  const isCancelledByAppUser =
    cancellationActor === "app_user" || cancellationActor === "app__user";
  const fineChargedTo = transaction?.fine_charged_to || (isCancelledByAppUser ? "app_user" : "provider");
  const hasDelayKeyword = (value) => {
    const normalized = String(value || "").toLowerCase();
    return normalized.includes("delay") || normalized.includes("provider_delay") || normalized.includes("تأخير");
  };
  const isDelayFine =
    transaction?.provider_delay_deduction === true ||
    String(transaction?.fine_source_type || "").toLowerCase() === "provider_delay" ||
    String(transaction?.fine_penalty_type || "").toLowerCase() === "provider_delay" ||
    String(transaction?.penalty_type || "").toLowerCase() === "provider_delay" ||
    String(transaction?.latest_delay_record_id || "").length > 0 ||
    String(transaction?.latest_penalty_id || "").length > 0 ||
    hasDelayKeyword(transaction?.fine_last_reason) ||
    hasDelayKeyword(transaction?.fine_reason) ||
    (Array.isArray(transaction?.deductions) &&
      transaction.deductions.some((item) => hasDelayKeyword(item?.type) || hasDelayKeyword(item?.reason)));
  const fineTypeLabel = isDelayFine ? "غرامة تأخير" : "غرامة إلغاء";
  const refundAppliedFine = fineChargedTo === "app_user" ? fineDeductions : 0;
  const isRefundSettlement =
    transaction?.is_refund_settlement === true ||
    transaction?.needs_refund === true ||
    transaction?.refund_status === "pending" ||
    isCancelledByAppUser;
  const platformRevenueForDisplay = isRefundSettlement ? 0 : totalPlatformRevenue;
  const appCommissionForDisplay = isRefundSettlement ? 0 : appCommission;
  const serviceProviderCommissionForDisplay = isRefundSettlement ? 0 : serviceProviderCommission;
  const tamaraCommissionForDisplay = isRefundSettlement ? 0 : tamaraCommission;
  const totalCommissionForDisplay = isRefundSettlement ? 0 : totalCommission;
  const commissionVATForDisplay = isRefundSettlement ? 0 : commissionVAT;
  const finalPayoutForDisplay = Math.max(grossAmount - refundAppliedFine, 0);

  return (
    <>
      {/* Main Payment Modal */}
      <Modal isOpen={isOpen} toggle={toggleModal} size="lg" centered>
        <ModalHeader toggle={toggleModal} className="border-0 pb-0">
          <h4 className="modal-title">تأكيد إنشاء فاتورة عمولة ERP</h4>
          <small className="text-muted">رقم المرجع: {transaction.id?.slice(0, 8) || 'N/A'}</small>
        </ModalHeader>
        
        <ModalBody>
          <Row>
              <Col md={6}>
                <Card className="mb-3">
                  <CardBody>
                    <CardTitle tag="h5" className="border-bottom pb-2 mb-3">
                      <i className="bx bx-coin me-2"></i>
                      إيرادات المنصة (ERP)
                    </CardTitle>
                    <div className="commission-breakdown">
                      <div className="d-flex justify-content-between mb-2">
                        <span className="fw-bold">إجمالي قيمة الخدمة:</span>
                        <span>{grossAmount.toFixed(2)} ريال</span>
                      </div>
                      <hr className="my-2" />
                      <div className="d-flex justify-content-between mb-2">
                        <span className="fw-bold">ضريبة الخدمة (15%):</span>
                        <span className="text-muted">{serviceVAT.toFixed(2)} ريال</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span className="fw-bold">صافي الخدمة:</span>
                        <span className="text-primary">{serviceNet.toFixed(2)} ريال</span>
                      </div>
                      <hr className="my-2" />
                      <div className="alert alert-light p-2 mb-2">
                        <small className="text-muted">عمولات المنصة:</small>
                        <div className="d-flex justify-content-between mb-1 mt-1">
                          <span className="small">عمولة التطبيق ({appCommissionRate}%):</span>
                          <span className="text-danger small">{appCommissionForDisplay.toFixed(2)} ريال</span>
                        </div>
                        <div className="d-flex justify-content-between mb-1">
                          <span className="small">عمولة مقدم الخدمة ({serviceProviderCommissionRate}%):</span>
                          <span className="text-danger small">{serviceProviderCommissionForDisplay.toFixed(2)} ريال</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="small">عمولة تمارا ({tamaraCommissionRate}%):</span>
                          <span className="text-danger small">{tamaraCommissionForDisplay.toFixed(2)} ريال</span>
                        </div>
                        {isRefundSettlement && (
                          <small className="text-muted d-block mt-1">في حالة الإلغاء/الاسترجاع لا يتم احتساب عمولة على التسوية</small>
                        )}
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span className="fw-bold">إجمالي العمولات:</span>
                        <span className="text-danger">{totalCommissionForDisplay.toFixed(2)} ريال</span>
                      </div>
                      <div className="d-flex justify-content-between mb-3">
                        <span className="fw-bold">ضريبة العمولات (15%):</span>
                        <span className="text-danger">{commissionVATForDisplay.toFixed(2)} ريال</span>
                      </div>
                      <div className="alert alert-success p-3 mb-0">
                        <div className="d-flex justify-content-between">
                          <span className="fw-bold text-success">
                            {isRefundSettlement ? "المحتسب كإيراد للمنصة:" : "إجمالي إيراد المنصة:"}
                          </span>
                          <span className="fw-bold text-success display-6">{platformRevenueForDisplay.toFixed(2)} ريال</span>
                        </div>
                        {isRefundSettlement && (
                          <small className="text-muted d-block mt-1">هذه معاملة استرجاع ولا تُحتسب كإيراد منصة</small>
                        )}
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="mb-3">
                  <CardBody>
                    <CardTitle tag="h5" className="border-bottom pb-2 mb-3">
                      <i className="bx bx-calendar me-2"></i>
                      معلومات المعاملة
                    </CardTitle>
                    <div className="transaction-info">
                      <div className="d-flex justify-content-between mb-3">
                        <span className="fw-bold">تاريخ العملية:</span>
                        <span>{formatDate(transaction.created_at)}</span>
                      </div>
                      {/* <div className="d-flex justify-content-between mb-3">
                        <span className="fw-bold">الإلغاء من:</span>
                        <Badge color={isCancelledByAppUser ? "info" : "warning"}>
                          {isCancelledByAppUser ? "العميل" : "مقدم الخدمة"}
                        </Badge>
                      </div> */}
                      <div className="alert alert-info p-2 mb-3">
                        <small className="text-muted">
                          <i className="bx bx-info-circle me-1"></i>
                          سيتم إنشاء فاتورة ERP للعمولات مباشرة لمقدم الخدمة كعميل
                        </small>
                      </div>
                    </div>

                    <CardTitle tag="h5" className="border-bottom pb-2 mb-3 mt-3">
                      <i className="bx bx-user me-2"></i>
                      معلومات الخدمة
                    </CardTitle>
                    <div className="service-info">
                      <div className="d-flex justify-content-between mb-2">
                        <span className="fw-bold">اسم الخدمة:</span>
                        <span>{transaction.serviceProviderServices?.name || 'غير محدد'}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span className="fw-bold">مقدم الخدمة:</span>
                        <span>{transaction.serviceProviders?.salon_name || 'غير محدد'}</span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="fw-bold">العميل:</span>
                        <span>{transaction.app_user_id?.username || 'غير محدد'}</span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

          {fineDeductions > 0 && (
            <Alert color="warning" className="mt-2">
              <i className="bx bx-info-circle me-2"></i>
              تم خصم {fineTypeLabel} بمبلغ
              <strong className="mx-1">{fineDeductions.toFixed(2)} ريال</strong>
              {isDelayFine
                ? " بسبب تأخير مقدم الخدمة. سيتم تحميلها على مقدم الخدمة في التسوية."
                : isCancelledByAppUser
                ? " بسبب إلغاء العميل. سيتم خصمها من مبلغ الاسترجاع للعميل."
                : " بسبب إلغاء مقدم الخدمة. سيتم تحميلها على مقدم الخدمة في التسوية."}
              <span className="d-block mt-1 text-muted">
                {isDelayFine
                  ? "وسيظهر سبب الخصم في التسوية كغرامة تأخير مقدم الخدمة."
                  : isCancelledByAppUser
                  ? "وسيتم احتساب صافي الاسترجاع بعد خصم الغرامة."
                  : "وسيتم رد كامل مبلغ العميل دون خصم غرامة عليه."}
              </span>
              {transaction?.fine_last_reason && (
                <span className="d-block mt-1 text-muted">
                  السبب: {transaction.fine_last_reason}
                </span>
              )}
            </Alert>
          )}

          <Alert color="success" className="mt-3">
            <i className="bx bx-check-circle me-2"></i>
            {isRefundSettlement ? (
              <>
                سيتم إنشاء تسوية بعلامة <strong>استرجاع</strong>، والمبلغ <strong>{(transaction?.amounts?.final_payout || finalPayoutForDisplay || 0).toFixed(2)} ريال</strong> لن يُحتسب كإيراد منصة.
              </>
            ) : (
              <>
                سيتم إنشاء فاتورة ERP للعمولات بإجمالي <strong>{platformRevenueForDisplay.toFixed(2)} ريال</strong> (عمولة التطبيق + عمولة مقدم الخدمة + عمولة تمارا + الضرائب)
              </>
            )}
          </Alert>
        </ModalBody>

        <ModalFooter className="border-0">
          <Button color="secondary" onClick={toggleModal} className="me-2">
            إلغاء
          </Button>
          <Button color="success" onClick={handleConfirm}>
            <i className="bx bx-check-circle me-1"></i>
            إنشاء فاتورة العمولة
          </Button>
        </ModalFooter>
      </Modal>

      {/* Confirmation Modal */}
      <Modal isOpen={isConfirmationOpen} toggle={handleCancel} centered>
        <ModalHeader toggle={handleCancel}>تأكيد إنشاء فاتورة عمولة ERP</ModalHeader>
        <ModalBody>
          <div className="text-center py-3">
            <i className="bx bx-check-circle display-4 text-success mb-3"></i>
            <h5>هل أنت متأكد من رغبتك في إنشاء فاتورة العمولة؟</h5>
            <p className="text-muted mt-2 mb-3">
              {isRefundSettlement ? (
                <>
                  سيتم إنشاء تسوية <strong className="text-danger">استرجاع</strong> بقيمة نهائية <strong className="text-danger">{(transaction?.amounts?.final_payout || netAfterFines || 0).toFixed(2)} ريال</strong> (غير محتسبة كإيراد)
                </>
              ) : (
                <>
                  سيتم إنشاء فاتورة ERP بإجمالي إيراد <strong className="text-success">{platformRevenueForDisplay.toFixed(2)} ريال</strong>
                </>
              )}
            </p>
            
            <Card className="bg-light mt-3">
              <CardBody>
                <Row>
                  <Col md={6} className="text-start border-end">
                    <small className="text-muted">قيمة الخدمة:</small>
                    <p className="fw-bold">{grossAmount.toFixed(2)} ريال</p>
                  </Col>
                  <Col md={6} className="text-end">
                    <small className="text-muted">{isRefundSettlement ? "المحتسب كإيراد:" : "إيراد المنصة:"}</small>
                    <p className="fw-bold text-success">{platformRevenueForDisplay.toFixed(2)} ريال</p>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <div className="mt-3 text-start">
              <Card className="bg-white border-success">
                <CardBody className="p-2">
                  <small className="text-muted d-block mb-1">تفصيل العمولات:</small>
                  <div className="d-flex justify-content-between mb-1">
                    <small>عمولة التطبيق:</small>
                    <small className="text-danger">{appCommissionForDisplay.toFixed(2)} ريال</small>
                  </div>
                  <div className="d-flex justify-content-between mb-1">
                    <small>عمولة مقدم الخدمة:</small>
                    <small className="text-danger">{serviceProviderCommissionForDisplay.toFixed(2)} ريال</small>
                  </div>
                  <div className="d-flex justify-content-between mb-1">
                    <small>عمولة تمارا:</small>
                    <small className="text-danger">{tamaraCommissionForDisplay.toFixed(2)} ريال</small>
                  </div>
                  <div className="d-flex justify-content-between">
                    <small>الضريبة (15%):</small>
                    <small className="text-danger">{commissionVATForDisplay.toFixed(2)} ريال</small>
                  </div>
                </CardBody>
              </Card>
            </div>

            {isSubmitting && (
              <Alert color="info" className="mt-3 text-start">
                <Spinner size="sm" className="me-2" />
                جاري إنشاء الفاتورة... يرجى عدم الإغلاق أو الضغط المتكرر.
              </Alert>
            )}
          </div>
        </ModalBody>
        <ModalFooter className="justify-content-center border-0">
          <Button color="secondary" onClick={handleCancel} className="px-4" disabled={isSubmitting}>
            <i className="bx bx-x me-1"></i>
            إلغاء
          </Button>
          <Button color="success" onClick={handleProceed} className="px-4 me-2" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Spinner size="sm" className="me-1" />
                جاري الإنشاء...
              </>
            ) : (
              <>
                <i className="bx bx-check me-1"></i>
                نعم، أنشئ الفاتورة
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default PayModal;